import SideMenuButton from "@navigation/SideMenuButton";
import MenuButton from "./MenuButton";
import MenuInception from "./MenuInception";
import Icon1 from "@assets/icons/Icon";
import { Project, ProjectDocument, ProjectHistoryEvent, ProjectPhoto } from "types";
import { useState, useEffect } from "react";
import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import { ActionsMenuButton } from "./ActionsMenuButton";
import { ModifyProjectModal } from "@components/modals/ModifyProjectModal";
import { DropFileModal, FolderOption, ControlPointOption, DropFileModalSaveData } from "@components/modals/DropFileModal";
import { EmailModal } from "@components/modals/EmailModal";
import { CompteRenduModal } from "@components/modals/CompteRenduModal";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { projectsApi } from "@services/api";
import { usersApi } from "@services/api/users";
import { invitationsApi } from "@services/api/invitations";
import { Timestamp } from "firebase/firestore";
import { compressImages } from "@utils/compressImage";
import { showNotification } from "context/notification.service";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { navigateTo } from "context/navigation.service";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { EditClientInfosModal } from "@components/modals/EditClientInfosModal";

const buildFolderOptions = (project: Project): FolderOption[] => {
  const options: FolderOption[] = [];
  for (const folder of project.folders) {
    if (folder.name === "Décennales") continue;
    options.push({
      value: folder.id,
      label: folder.name,
    });
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        options.push({
          value: `${folder.id}/${sub.id}`,
          label: `${folder.name} / ${sub.name}`,
        });
      }
    }
  }
  return options;
};

const buildControlPointOptions = (project: Project): ControlPointOption[] => {
  const options: ControlPointOption[] = [];
  for (const theme of project.themes) {
    for (const step of theme.steps) {
      for (const cp of step.controlpoints) {
        options.push({
          value: cp.id,
          label: `${step.label} - ${cp.label}`,
        });
      }
    }
  }
  return options;
};

export const ActionsMenuButtonProject = ({
  project,
  setProject,
  isGuest = false,
  onSignatureRequestCreated,
}: {
  project: Project;
  setProject: (project: Project) => void;
  isGuest?: boolean;
  onSignatureRequestCreated?: () => void;
}) => {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const { sentEmails, setSentEmails, removeProjects } = useProjectStore();
  const [modalsOpen, setModalsOpen] = useState<{
    modify: boolean;
    delete: boolean;
    addDocument: boolean;
    addPhoto: boolean;
    compteRendu: boolean;
    changeStatus: boolean;
    sendEmail: boolean;
  }>({
    modify: false,
    delete: false,
    addDocument: false,
    addPhoto: false,
    compteRendu: false,
    changeStatus: false,
    sendEmail: false,
  });

  const [editClientInfosOpen, setEditClientInfosOpen] = useState(false);

  // Fetch contact metadata for CLIENT section
  const [contactsMetadata, setContactsMetadata] = useState<
    Map<string, { status: string; has_third_party: boolean }>
  >(new Map());

  useEffect(() => {
    if (isGuest) return;
    const contactsWithUser = project.infos.client.contacts.filter(
      (c) => c.user_id,
    );
    if (contactsWithUser.length === 0) return;
    contactsWithUser.forEach(async (contact) => {
      try {
        const user = await usersApi.get(contact.user_id!);
        setContactsMetadata((prev) => {
          const next = new Map(prev);
          next.set(contact.user_id!, {
            status: user.status || "active",
            has_third_party: !!(user.infos.third_party_providers?.length || user.infos.third_party),
          });
          return next;
        });
      } catch {
        // ignore
      }
    });
  }, [project.infos.client.contacts, isGuest]);

  // Button 1 — send the invitation email (the accept-invitation link) so the
  // person can create/repair a login method. Works whether or not the contact
  // already has an account.
  const handleSendInvitation = async (contact: {
    user_id: string;
    first_name: string;
    last_name: string;
    email?: string;
  }) => {
    try {
      if (contact.user_id) {
        await invitationsApi.resend(contact.user_id);
      } else if (contact.email) {
        const existingUser = await usersApi.lookup(contact.email);
        if (existingUser) {
          await invitationsApi.resend(existingUser.id);
        } else {
          // No account yet — send a fresh invitation.
          await invitationsApi.create({
            invitee_email: contact.email,
            invitee_name: `${contact.first_name} ${contact.last_name}`.trim() || undefined,
            invitation_type: "web",
            context: { access_type: "GUEST", project_id: project.id },
          });
        }
      }
      showNotification({
        type: "success",
        message: `Invitation envoyée à ${contact.first_name} ${contact.last_name}`,
      });
    } catch (error) {
      console.error("Error sending invitation:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi de l'invitation",
      });
    }
  };

  // Button 2 — send the project access email with the app/project link (for a
  // person who already has a login and just needs to open/download the app).
  const handleSendAppLink = async (contact: {
    user_id: string;
    first_name: string;
    last_name: string;
    email?: string;
  }) => {
    try {
      let targetUserId = contact.user_id;
      if (!targetUserId && contact.email) {
        const existingUser = await usersApi.lookup(contact.email);
        if (!existingUser) {
          showNotification({
            type: "error",
            message:
              "Ce contact n'a pas encore de compte — envoyez-lui d'abord une invitation.",
          });
          return;
        }
        targetUserId = existingUser.id;
        // Link the contact, not the guest list: the backend derives
        // guest_user_ids from the contacts. Sending {guest_user_ids: [id]}
        // here used to REPLACE the whole list, revoking every other client on
        // the project (e.g. the spouse on a two-contact dossier).
        const contacts = (project.infos?.client?.contacts || []).map((c) =>
          c.email === contact.email && !c.user_id ? { ...c, user_id: targetUserId } : c,
        );
        await projectsApi.update(project.id, {
          infos: { ...project.infos, client: { ...project.infos?.client, contacts } },
        });
      }
      if (!targetUserId) return;
      await projectsApi.sendAccessEmail(project.id, targetUserId, "app");
      showNotification({
        type: "success",
        message: `Lien de l'application envoyé à ${contact.first_name} ${contact.last_name}`,
      });
    } catch (error) {
      console.error("Error sending app link:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi du lien",
      });
    }
  };

  const handleResetPassword = async (contact: {
    user_id: string;
    first_name: string;
    last_name: string;
  }) => {
    try {
      await usersApi.sendPasswordReset(contact.user_id);
      showNotification({
        type: "success",
        message: `Email de réinitialisation envoyé à ${contact.first_name} ${contact.last_name}`,
      });
    } catch (error) {
      console.error("Error sending password reset:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de l'envoi de l'email de réinitialisation",
      });
    }
  };

  const handleAddDocuments = async (data: DropFileModalSaveData) => {
    setModalsOpen({ ...modalsOpen, addDocument: false });

    try {
      const now = Timestamp.now();

      // Create temporary document entries (will be replaced by backend response)
      const tempDocuments: ProjectDocument[] = data.files.map((file) => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        name: file.name,
        file: {
          id: '',
          url: '',
          name: file.name,
          created_at: now,
          octets: file.size,
        },
        hidden: data.internalDocument,
      }));

      // repertory value is either "folderId" or "folderId/subfolderId"
      const parts = data.repertory.split("/");
      const folderId = parts[0];
      const subfolderId = parts[1];

      let updatedFolders = project.folders.map((folder) => {
        if (folder.id !== folderId) return folder;

        if (subfolderId && folder.sub_folders) {
          return {
            ...folder,
            sub_folders: folder.sub_folders.map((sub) =>
              sub.id === subfolderId
                ? {
                    ...sub,
                    documents: [...(sub.documents || []), ...tempDocuments],
                  }
                : sub,
            ),
          };
        }

        return {
          ...folder,
          documents: [...(folder.documents || []), ...tempDocuments],
        };
      });

      const updatedProject = await projectsApi.addDocuments(
        project.id,
        data.files,
        folderId,
        subfolderId,
        data.internalDocument,
      );

      setProject(updatedProject);

      showNotification({
        type: "success",
        message: `${tempDocuments.length} document${tempDocuments.length > 1 ? "s" : ""} ajouté${tempDocuments.length > 1 ? "s" : ""} avec succès`,
      });

      // If electronic signature was requested, create one signature request
      // per uploaded doc with the pre-selected signers.
      if (data.electronicSignature && data.signers && data.signers.length > 0) {
        const allDocs: { id: string; name: string; file: { id: string; url: string; name: string } }[] = [];
        for (const folder of updatedProject.folders) {
          for (const doc of folder.documents || []) allDocs.push(doc);
          for (const sub of folder.sub_folders || []) {
            for (const doc of sub.documents || []) allDocs.push(doc);
          }
        }
        const uploadedDocs = data.files
          .map((f) => allDocs.find((d) => d.name === f.name))
          .filter((d) => d?.file?.id)
          .map((d) => ({ fileId: d!.file.id, documentName: d!.name }));

        const signerPayload = data.signers.map((s) => ({
          id: s.id,
          signer_name: s.signer_name,
          signer_email: s.signer_email,
          signer_type: s.signer_type,
          // Forward initials (auto-derived from the signer name in the
          // picker, optionally edited). Without this the conducteur's
          // auto-painted block in the inline-fields signature loses its
          // initials line, and remote signers don't get a pre-filled
          // value to confirm.
          initials: s.initials || undefined,
        }));

        // Forward the placement mode + inline fields from the drop modal.
        const useInline = data.placementMode === "inline_fields";
        const inlineApiFields = useInline && data.inlineFields
          ? data.inlineFields.map((f) => ({
              type: f.type as "Signature" | "Text" | "Date" | "Tampon",
              x: f.left,
              y: f.top,
              width: f.width,
              height: f.height,
              page: f.page,
              label: f.name,
              content: f.content,
              signer_id: f.signer_id,
            }))
          : undefined;

        let successCount = 0;
        for (const doc of uploadedDocs) {
          try {
            await signatureRequestsApi.create({
              project_id: project.id,
              org_id: orgData?.id || "",
              document_name: doc.documentName,
              file_id: doc.fileId,
              signers: signerPayload,
              placement_mode: useInline ? "inline_fields" : "appended_page",
              fields: useInline ? inlineApiFields : undefined,
              target_width: useInline ? data.inlineDimensions?.width : undefined,
              target_height: useInline ? data.inlineDimensions?.height : undefined,
            });
            successCount++;
          } catch (e) {
            console.error(`Failed to create signature for ${doc.documentName}:`, e);
          }
        }
        if (successCount > 0) {
          showNotification({
            type: "success",
            message: `${successCount} document${successCount > 1 ? "s" : ""} envoyé${successCount > 1 ? "s" : ""} pour signature`,
          });
          onSignatureRequestCreated?.();
        }
        if (successCount < uploadedDocs.length) {
          showNotification({
            type: "error",
            message: `Échec de l'envoi pour ${uploadedDocs.length - successCount} document(s)`,
          });
        }
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      showNotification({
        type: "error",
        message: "Erreur lors du téléchargement des documents",
      });
    }
  };

  const handleAddPhotos = async (data: {
    files: File[];
    repertory: string;
    electronicSignature: boolean;
    internalDocument: boolean;
    controlpointId?: string;
  }) => {
    setModalsOpen({ ...modalsOpen, addPhoto: false });

    try {
      const now = Timestamp.now();

      // Create temporary photo entries (will be replaced by backend response)
      const tempPhotos: ProjectPhoto[] = data.files.map((file) => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        file: {
          id: '',
          url: '',
          name: file.name,
          created_at: now,
          octets: file.size,
        },
        hidden: data.internalDocument,
      }));

      const tempPhotoIds = tempPhotos.map((p) => p.id);

      // If a controlpoint is selected, link photos to it
      let updatedThemes = project.themes;
      if (data.controlpointId) {
        updatedThemes = project.themes.map((theme) => ({
          ...theme,
          steps: theme.steps.map((step) => ({
            ...step,
            controlpoints: step.controlpoints.map((cp) =>
              cp.id === data.controlpointId
                ? {
                    ...cp,
                    photos_id: [...(cp.photos_id || []), ...tempPhotoIds],
                  }
                : cp,
            ),
          })),
        }));
      }

      const compressedFiles = await compressImages(data.files);
      const updatedProject = await projectsApi.addPhotos(
        project.id,
        compressedFiles,
        data.controlpointId,
        data.internalDocument,
      );

      setProject(updatedProject);

      showNotification({
        type: "success",
        message: `${tempPhotos.length} photo${tempPhotos.length > 1 ? "s" : ""} ajoutée${tempPhotos.length > 1 ? "s" : ""} avec succès`,
      });
    } catch (error) {
      console.error("Error uploading photos:", error);
      showNotification({
        type: "error",
        message: "Erreur lors du téléchargement des photos",
      });
    }
  };

  return (
    <>
      {isGuest && editClientInfosOpen && (() => {
        let contactIndex = project.infos.client.contacts.findIndex(
          (c) => c.user_id === userData?.id,
        );
        if (contactIndex < 0 && userData?.infos?.email) {
          contactIndex = project.infos.client.contacts.findIndex(
            (c) => c.email === userData.infos.email,
          );
        }
        if (contactIndex < 0) contactIndex = 0;
        return (
          <EditClientInfosModal
            onClose={() => setEditClientInfosOpen(false)}
            project={project}
            setProject={setProject}
            contactIndex={contactIndex}
          />
        );
      })()}
      {!isGuest && modalsOpen.modify && (
        <ModifyProjectModal
          project={project}
          setProject={setProject}
          onClose={() => setModalsOpen({ ...modalsOpen, modify: false })}
        ></ModifyProjectModal>
      )}
      {!isGuest && modalsOpen.delete && (
        <ConfirmationModal
          title={`Supprimer le projet "${project.infos.general.name}" ?`}
          message="Cette action est irréversible. Tous les documents, photos et données associés seront supprimés."
          confirmText="CONFIRMER"
          onClose={() => setModalsOpen({ ...modalsOpen, delete: false })}
          onConfirm={() => {
            removeProjects([project.id]);
            showNotification({
              type: "success",
              message: "Projet supprimé avec succès",
            });
            setModalsOpen({ ...modalsOpen, delete: false });
            navigateTo({ route: "/projects" });
          }}
        />
      )}
      {modalsOpen.addDocument && (
        <DropFileModal
          onClose={() => setModalsOpen({ ...modalsOpen, addDocument: false })}
          onSave={handleAddDocuments}
          folderOptions={buildFolderOptions(project)}
          showElectronicSignature={!isGuest}
          showInternalDocument={!isGuest}
          project={project}
        ></DropFileModal>
      )}
      {modalsOpen.addPhoto && (
        <DropFileModal
          title="Ajouter des photos"
          iconName="image"
          onClose={() => setModalsOpen({ ...modalsOpen, addPhoto: false })}
          onSave={handleAddPhotos}
          saveButtonLabel="Ajouter"
          showRepertorySelector={false}
          showElectronicSignature={false}
          showInternalDocument={!isGuest}
          showImagePreview={true}
          acceptedFileTypes="image/*"
        />
      )}
      {modalsOpen.sendEmail && (
        <EmailModal
          onClose={() => setModalsOpen({ ...modalsOpen, sendEmail: false })}
          activeProject={project}
          setActiveProject={setProject}
          userData={userData || undefined}
          orgData={orgData || undefined}
          sentEmails={sentEmails}
          setSentEmails={setSentEmails}
          isGuest={isGuest}
        />
      )}
      {!isGuest && modalsOpen.compteRendu && (
        <CompteRenduModal
          onClose={() => setModalsOpen({ ...modalsOpen, compteRendu: false })}
          project={project}
          setProject={setProject}
          orgId={orgData?.id || ""}
          onSignatureRequestCreated={onSignatureRequestCreated}
        />
      )}
      <div className="flex flex-row items-center gap-2">
        <button
          className="flex items-center justify-center w-10 h-10 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer transition-colors"
          title="Ajouter un document"
          onClick={() => setModalsOpen({ ...modalsOpen, addDocument: true })}
        >
          <Icon1 name="plus" size={18} />
        </button>
        <button
          className="flex items-center justify-center w-10 h-10 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer transition-colors"
          title="Envoyer un email"
          onClick={() => setModalsOpen({ ...modalsOpen, sendEmail: true })}
        >
          <Icon1 name="email" size={18} />
        </button>
        <ActionsMenuButton
          project={project}
          setProject={setProject}
          modalsOpen={modalsOpen}
          setModalsOpen={setModalsOpen}
          userData={userData}
          isGuest={isGuest}
          contactsMetadata={contactsMetadata}
          onSendInvitation={handleSendInvitation}
          onSendAppLink={handleSendAppLink}
          onResetPassword={handleResetPassword}
          onEditClientInfos={() => setEditClientInfosOpen(true)}
        ></ActionsMenuButton>
      </div>
    </>
  );
};
